import { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';

import { NEW_UI_DOCUMENT_CLASS } from '@/constants';
import { useNewUiFeatureFlag } from '@/shared/hooks/useNewUiFeatureFlag';

export type Theme = 'dark' | 'light' | 'system';

type ThemeProviderState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
};

export const THEME_STORAGE_KEY = 'lynk/theme';

const ThemeProviderContext = createContext<ThemeProviderState>({
  theme: 'system',
  setTheme: () => {},
});

export function ThemeProvider({ children }: PropsWithChildren) {
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem(THEME_STORAGE_KEY) as Theme) || 'light',
  );
  const isNewUi = useNewUiFeatureFlag();

  /*
   * This useEffect conditionally adds css styles needed by the new UI.
   * We are aiming to deploy the new UI to production incrementally by Access Context.
   * To use the new UI users need to enable the feature and be logged in with a supported Context
   * TODO ENG-3377: Delete this useEffect
   */
  useEffect(() => {
    const root = window.document.documentElement;
    if (isNewUi) {
      root.classList.add(NEW_UI_DOCUMENT_CLASS);
    }
    return () => root.classList.remove(NEW_UI_DOCUMENT_CLASS);
  }, [isNewUi]);

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');

    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';

      root.classList.add(systemTheme);
      return;
    }

    root.classList.add(theme);
  }, [theme]);

  const value = useMemo(
    () => ({
      theme,
      setTheme: (newTheme: Theme) => {
        localStorage.setItem(THEME_STORAGE_KEY, newTheme);
        setTheme(newTheme);
      },
    }),
    [theme],
  );

  return <ThemeProviderContext.Provider value={value}>{children}</ThemeProviderContext.Provider>;
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
